'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { Client } from '@/lib/api/clients/client-api'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import {
    createNfe,
    CreateNfeData,
    FiscalReadiness,
    getFiscalReadiness,
    NfeItem,
} from '@/lib/api/fiscal/fiscal-api'
import { fetchCnpj } from '@/lib/api/fetch-cnpj'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdClose,
    MdDelete,
    MdSearch,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type TomadorSource = 'client' | 'partner' | 'manual'

const UNIT_MAP: Record<string, string> = {
    UNIT: 'UN',
    KG: 'KG',
    GRAM: 'G',
    LITER: 'L',
    ML: 'ML',
    METER: 'M',
    CM: 'CM',
    HOUR: 'HR',
    SESSION: 'UN',
    PACK: 'PC',
    BOX: 'CX',
    PAIR: 'PAR',
    DOZEN: 'DZ',
}

const emptyItem = (): NfeItem => ({
    descricao: '',
    codigoProduto: '',
    cfop: '5102',
    unidadeComercial: 'UN',
    quantidadeComercial: 1,
    valorUnitarioComercial: 0,
})

const NfeFormPage = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [readiness, setReadiness] = useState<FiscalReadiness | null>(null)
    const [readinessLoading, setReadinessLoading] = useState(true)

    useEffect(() => {
        const checkReadiness = async () => {
            const res = await getFiscalReadiness()
            if (res.success) setReadiness(res.result || null)
            setReadinessLoading(false)
        }
        checkReadiness()
    }, [])

    // Destinatario
    const [tomadorSource, setTomadorSource] = useState<TomadorSource>('manual')
    const [selectedClientId, setSelectedClientId] = useState<number | null>(
        null
    )
    const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
        null
    )


    // Form
    const [formData, setFormData] = useState<Omit<CreateNfeData, 'items'>>({
        naturezaOperacao: 'Venda de mercadoria',
        destinatarioCnpjCpf: '',
        destinatarioRazaoSocial: '',
    })
    const [items, setItems] = useState<NfeItem[]>([emptyItem()])

    const update = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const updateItem = (index: number, field: keyof NfeItem, value: any) => {
        setItems(prev => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
    }

    const addItem = () => setItems(prev => [...prev, emptyItem()])

    const removeItem = (index: number) => {
        if (items.length <= 1) return
        setItems(prev => prev.filter((_, i) => i !== index))
    }

    const formatCentsInput = (value: string): number => {
        const numbers = value.replace(/\D/g, '')
        return parseInt(numbers) || 0
    }

    const formatCurrency = (cents: number) =>
        new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100)

    const totalGeral = items.reduce(
        (sum, item) =>
            sum + item.valorUnitarioComercial * item.quantidadeComercial,
        0
    )

    // === Destinatario handlers ===

    const handleTomadorSourceChange = (source: TomadorSource) => {
        setTomadorSource(source)
        setSelectedClientId(null)
        setSelectedPartnerId(null)
        setFormData(prev => ({
            ...prev,
            clientId: undefined,
            destinatarioCnpjCpf: '',
            destinatarioRazaoSocial: '',
        }))
    }

    const handleClientSelect = (_id: number | null, client: Client | null) => {
        setSelectedClientId(client?.id || null)
        if (client) {
            setFormData(prev => ({
                ...prev,
                clientId: client.id,
                destinatarioCnpjCpf: client.cpf || '',
                destinatarioRazaoSocial: client.name,
            }))
        }
    }

    const handlePartnerSelect = (
        _id: number | null,
        partner: Partner | null
    ) => {
        setSelectedPartnerId(partner?.id || null)
        if (partner) {
            setFormData(prev => ({
                ...prev,
                destinatarioCnpjCpf: partner.cnpj || partner.cpf || '',
                destinatarioRazaoSocial: partner.name,
            }))
        }
    }

    const [cnpjLoading, setCnpjLoading] = useState(false)

    const handleCnpjBlur = async () => {
        if (tomadorSource !== 'manual') return
        const clean = formData.destinatarioCnpjCpf.replace(/\D/g, '')
        if (clean.length !== 14) return
        setCnpjLoading(true)
        const data = await fetchCnpj(clean)
        if (data) {
            setFormData(prev => ({
                ...prev,
                destinatarioRazaoSocial:
                    prev.destinatarioRazaoSocial || data.razaoSocial,
            }))
        }
        setCnpjLoading(false)
    }

    // === Submit ===

    const handleSubmit = async () => {
        if (
            !formData.destinatarioCnpjCpf ||
            !formData.destinatarioRazaoSocial
        ) {
            toast.error('Preencha os dados do destinatário')
            return
        }
        if (items.some(i => !i.descricao || !i.valorUnitarioComercial)) {
            toast.error('Preencha todos os campos obrigatórios dos itens')
            return
        }

        setLoading(true)
        const response = await createNfe({ ...formData, items })
        if (response.success) {
            toast.success('NF-e enviada para processamento')
            navigate('/fiscal/invoices')
        } else {
            toast.error(response.error)
        }
        setLoading(false)
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/fiscal/invoices')}
                >
                    <MdArrowBack size={20} />
                </button>
                <h1 className="text-2xl font-bold">Emitir NF-e</h1>
            </div>

            {readinessLoading && (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            )}

            {!readinessLoading && readiness && !readiness.nfe.ready && (
                <div className="alert alert-warning mb-6">
                    <MdWarning size={24} />
                    <div>
                        <h3 className="font-bold">
                            Configuração incompleta para emissão de NF-e
                        </h3>
                        <ul className="list-disc list-inside mt-1 text-sm">
                            {readiness.nfe.missingFields.map(field => (
                                <li key={field}>{field}</li>
                            ))}
                        </ul>
                        <div className="flex gap-2 mt-3">
                            {!readiness.isProvisioned && (
                                <button
                                    className="btn btn-sm btn-outline"
                                    onClick={() => navigate('/fiscal/settings')}
                                >
                                    Configurações fiscais
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={() => navigate('/conta?tab=company')}
                            >
                                Dados da empresa
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!readinessLoading && readiness?.nfe.ready && (
                <>
                    {/* Destinatario */}
                    <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                Destinatário
                            </h2>

                            <div className="flex gap-2 mb-4">
                                {(
                                    [
                                        'client',
                                        'partner',
                                        'manual',
                                    ] as TomadorSource[]
                                ).map(source => (
                                    <button
                                        key={source}
                                        className={`btn btn-sm ${tomadorSource === source ? 'btn-secondary' : 'btn-ghost'}`}
                                        onClick={() =>
                                            handleTomadorSourceChange(source)
                                        }
                                    >
                                        {source === 'client'
                                            ? 'Cliente'
                                            : source === 'partner'
                                              ? 'Parceiro'
                                              : 'Manual'}
                                    </button>
                                ))}
                            </div>

                            {tomadorSource === 'client' && (
                                <ClientSelector
                                    value={selectedClientId}
                                    onChange={handleClientSelect}
                                    label=""
                                    placeholder="Buscar cliente..."
                                    className="mb-4"
                                />
                            )}

                            {tomadorSource === 'partner' && (
                                <PartnerSelector
                                    value={selectedPartnerId}
                                    onChange={handlePartnerSelect}
                                    label=""
                                    placeholder="Buscar parceiro..."
                                    className="mb-4"
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            CNPJ/CPF *
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            value={formData.destinatarioCnpjCpf}
                                            onChange={e =>
                                                update(
                                                    'destinatarioCnpjCpf',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={handleCnpjBlur}
                                            placeholder="00.000.000/0000-00"
                                            readOnly={
                                                tomadorSource !== 'manual'
                                            }
                                        />
                                        {cnpjLoading && (
                                            <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"></span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Razão Social *
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={formData.destinatarioRazaoSocial}
                                        onChange={e =>
                                            update(
                                                'destinatarioRazaoSocial',
                                                e.target.value
                                            )
                                        }
                                        readOnly={tomadorSource !== 'manual'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Itens */}
                    <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                        <div className="card-body">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="card-title text-lg">Itens</h2>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={addItem}
                                >
                                    <MdAdd size={18} /> Adicionar
                                </button>
                            </div>


                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    className="border border-base-200 rounded-lg p-4 mb-4"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="font-semibold text-sm">
                                            Item {index + 1}
                                        </span>
                                        {items.length > 1 && (
                                            <button
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={() =>
                                                    removeItem(index)
                                                }
                                            >
                                                <MdDelete size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div className="form-control md:col-span-2">
                                            <label className="label">
                                                <span className="label-text">
                                                    Descrição *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm"
                                                value={item.descricao}
                                                onChange={e =>
                                                    updateItem(
                                                        index,
                                                        'descricao',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">
                                                    Código Produto
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm"
                                                value={item.codigoProduto}
                                                onChange={e =>
                                                    updateItem(
                                                        index,
                                                        'codigoProduto',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">
                                                    Quantidade
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered input-sm"
                                                value={item.quantidadeComercial}
                                                onChange={e =>
                                                    updateItem(
                                                        index,
                                                        'quantidadeComercial',
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                min={1}
                                                step="0.01"
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">
                                                    Valor Unitário *
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm"
                                                value={
                                                    item.valorUnitarioComercial
                                                        ? formatCurrency(
                                                              item.valorUnitarioComercial
                                                          )
                                                        : ''
                                                }
                                                onChange={e =>
                                                    updateItem(
                                                        index,
                                                        'valorUnitarioComercial',
                                                        formatCentsInput(
                                                            e.target.value
                                                        )
                                                    )
                                                }
                                                placeholder="R$ 0,00"
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">
                                                    Subtotal
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm bg-base-200"
                                                value={formatCurrency(
                                                    item.valorUnitarioComercial *
                                                        item.quantidadeComercial
                                                )}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="text-right mt-2">
                                <span className="text-lg font-bold">
                                    Total: {formatCurrency(totalGeral)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            className="btn btn-ghost"
                            onClick={() => navigate('/fiscal/invoices')}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            Emitir NF-e
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

export default NfeFormPage
